import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import {
  LocationSeo,
  LocationSeoCreateDto,
  LocationSeoUpdateDto,
  LocationSeoResponse
} from "../../modules/shared/model";
import {
  createLocationSeo as createLocationSeoAction,
  updateLocationSeo as updateLocationSeoAction,
  getLocationSeoById as getLocationSeoByIdAction,
  getLocationSeos as getLocationSeosAction,
  deleteLocationSeo as deleteLocationSeoAction,
  getLocationSeosByCountry as getLocationSeosByCountryAction,
  getLocationSeosByRegion as getLocationSeosByRegionAction,
  getLocationSeosByCity as getLocationSeosByCityAction
} from "../Actions/LocationSeoActions";

// Location SEO Async Thunks
export const createLocationSeo = createAsyncThunk("locationSeos/createLocationSeo", async (data: LocationSeoCreateDto, { rejectWithValue }) => {
  try { return await createLocationSeoAction(data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to create location SEO"); }
});

export const updateLocationSeo = createAsyncThunk("locationSeos/updateLocationSeo", async ({ id, data }: { id: string; data: LocationSeoUpdateDto }, { rejectWithValue }) => {
  try { return await updateLocationSeoAction(id, data); } catch (e: any) { return rejectWithValue(e?.message || "Failed to update location SEO"); }
});

export const fetchLocationSeo = createAsyncThunk("locationSeos/getLocationSeoById", async (id: string, { rejectWithValue }) => {
  try { return await getLocationSeoByIdAction(id); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch location SEO"); }
});

export const fetchLocationSeos = createAsyncThunk("locationSeos/getLocationSeos", async (query: Record<string, string | number | boolean> | undefined, { rejectWithValue }) => {
  try { return await getLocationSeosAction(query); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch location SEOs"); }
});

export const deleteLocationSeo = createAsyncThunk("locationSeos/deleteLocationSeo", async (id: string, { rejectWithValue }) => {
  try { await deleteLocationSeoAction(id); return id; } catch (e: any) { return rejectWithValue(e?.message || "Failed to delete location SEO"); }
});

export const fetchLocationSeosByCountry = createAsyncThunk("locationSeos/getLocationSeosByCountry", async (countryId: string, { rejectWithValue }) => {
  try { return await getLocationSeosByCountryAction(countryId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch location SEOs by country"); }
});

export const fetchLocationSeosByRegion = createAsyncThunk("locationSeos/getLocationSeosByRegion", async (regionId: string, { rejectWithValue }) => {
  try { return await getLocationSeosByRegionAction(regionId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch location SEOs by region"); }
});

export const fetchLocationSeosByCity = createAsyncThunk("locationSeos/getLocationSeosByCity", async (cityId: string, { rejectWithValue }) => {
  try { return await getLocationSeosByCityAction(cityId); } catch (e: any) { return rejectWithValue(e?.message || "Failed to fetch location SEOs by city"); }
});

interface LocationSeoState {
  locationSeos: LocationSeo[];
  currentLocationSeo: LocationSeo | null;
  loading: boolean;
  error: string | null;
  totalCount?: number;
  currentPage: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

const initialState: LocationSeoState = {
  locationSeos: [],
  currentLocationSeo: null,
  loading: false,
  error: null,
  totalCount: 0,
  currentPage: 1,
  hasNextPage: false,
  hasPreviousPage: false,
};

const locationSeoSlice = createSlice({
  name: "locationSeos",
  initialState,
  reducers: {
    clearLocationSeoError(state) { state.error = null; },
    clearCurrentLocationSeo(state) { state.currentLocationSeo = null; },
    clearLocationSeos(state) {
      state.locationSeos = [];
      state.currentLocationSeo = null;
      state.totalCount = 0;
    }
  },
  extraReducers: (builder) => {
    // Create Location SEO
    builder
      .addCase(createLocationSeo.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(createLocationSeo.fulfilled, (s, a: PayloadAction<LocationSeo>) => { 
        s.loading = false; 
        s.locationSeos.unshift(a.payload); 
        s.totalCount = (s.totalCount || 0) + 1; 
      })
      .addCase(createLocationSeo.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Update Location SEO
    builder
      .addCase(updateLocationSeo.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(updateLocationSeo.fulfilled, (s, a: PayloadAction<LocationSeo>) => {
        s.loading = false;
        const i = s.locationSeos.findIndex(x => x.id === a.payload.id);
        if (i !== -1) s.locationSeos[i] = a.payload;
        if (s.currentLocationSeo?.id === a.payload.id) s.currentLocationSeo = a.payload;
      })
      .addCase(updateLocationSeo.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Fetch Location SEO by ID
    builder
      .addCase(fetchLocationSeo.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchLocationSeo.fulfilled, (s, a: PayloadAction<LocationSeo>) => { s.loading = false; s.currentLocationSeo = a.payload; })
      .addCase(fetchLocationSeo.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Fetch Location SEOs
    builder
      .addCase(fetchLocationSeos.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchLocationSeos.fulfilled, (s, a: PayloadAction<LocationSeoResponse>) => { 
        s.loading = false; 
        s.locationSeos = a.payload.data || []; 
        s.totalCount = a.payload.totalCount || 0; 
        s.currentPage = a.payload.page || 1;
        s.hasNextPage = a.payload.hasNextPage || false;
        s.hasPreviousPage = a.payload.hasPreviousPage || false;
      })
      .addCase(fetchLocationSeos.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Delete Location SEO
    builder
      .addCase(deleteLocationSeo.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(deleteLocationSeo.fulfilled, (s, a: PayloadAction<string>) => { 
        s.loading = false; 
        s.locationSeos = s.locationSeos.filter(x => x.id !== a.payload); 
        if (s.currentLocationSeo?.id === a.payload) s.currentLocationSeo = null; 
        s.totalCount = Math.max(0, (s.totalCount || 0) - 1); 
      })
      .addCase(deleteLocationSeo.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Fetch Location SEOs by Country
    builder
      .addCase(fetchLocationSeosByCountry.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchLocationSeosByCountry.fulfilled, (s, a: PayloadAction<LocationSeo[]>) => { s.loading = false; s.locationSeos = a.payload || []; })
      .addCase(fetchLocationSeosByCountry.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Fetch Location SEOs by Region
    builder
      .addCase(fetchLocationSeosByRegion.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchLocationSeosByRegion.fulfilled, (s, a: PayloadAction<LocationSeo[]>) => { s.loading = false; s.locationSeos = a.payload || []; })
      .addCase(fetchLocationSeosByRegion.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });

    // Fetch Location SEOs by City
    builder
      .addCase(fetchLocationSeosByCity.pending, (s) => { s.loading = true; s.error = null; })
      .addCase(fetchLocationSeosByCity.fulfilled, (s, a: PayloadAction<LocationSeo[]>) => { s.loading = false; s.locationSeos = a.payload || []; })
      .addCase(fetchLocationSeosByCity.rejected, (s, a: any) => { s.loading = false; s.error = a.payload as string; });
  }
});

export const {
  clearLocationSeoError,
  clearCurrentLocationSeo,
  clearLocationSeos
} = locationSeoSlice.actions;
export default locationSeoSlice.reducer;
